import React, { FC, useState } from 'react'
import { Box, Button, Chip, FormControlLabel, Grid, Paper, Switch, TextField, Typography } from '@mui/material'
import { DataGridPremium, GridColDef, GridRowId } from '@mui/x-data-grid-premium'
import { UserContractor } from 'src/api'
import UserContractorColumns from 'src/components/columns/userContractor'
import { useUserContractorListRequest } from 'src/hooks/api'
import { useStableCount, useWizardGrid, WizardFooter } from './wizardParts'
import { TransferWizardAction, TransferWizardState, TransferWizardStep } from './transferWizardHelpers'

const columns: GridColDef<UserContractor>[] = [
  { ...UserContractorColumns.PlainName, valueGetter: ({ row }) => row.name || row.user?.name || '-' },
  { ...UserContractorColumns.Email, valueGetter: ({ row }) => row.email || row.user?.email || '-' },
  UserContractorColumns.Phone,
  UserContractorColumns.Role,
  {
    ...UserContractorColumns.Status,
    renderCell: ({ row }) => <Chip size="small" label={row.isArchived ? 'Archived' : 'Active'} color={row.isArchived ? 'default' : 'success'} variant="outlined" />
  }
]

interface UsersStepProps {
  state: TransferWizardState
  dispatch: (action: TransferWizardAction) => void
}

/** Step 2: the source memberships to move, picked by hand; only active ones can be picked. */
export const UsersStep: FC<UsersStepProps> = ({ state, dispatch }) => {
  const grid = useWizardGrid()
  // archived memberships cannot move; they are listed only on request
  const [showArchived, setShowArchived] = useState(false)
  const { data, isFetching, isError, error } = useUserContractorListRequest({
    params: { contractorId: state.source?.id, ...grid.requestParams, ...(showArchived ? {} : { isActive: true }) },
    options: { keepPreviousData: true }
  })
  const count = useStableCount(data?.count)
  const selectedCount = state.userContractorIds.length

  return (
    <>
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Search by name, email or phone" value={grid.search} onChange={(event) => grid.setSearch(event.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} gap={2} flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showArchived}
                  onChange={(event) => {
                    setShowArchived(event.target.checked)
                    grid.onPaginationModelChange({ page: 0, pageSize: grid.pageSize })
                  }}
                />
              }
              label="Show archived"
            />
            <Typography variant="body2">{`${selectedCount} selected`}</Typography>
            <Button size="small" disabled={!selectedCount} onClick={() => dispatch({ type: 'setUsers', ids: [] })}>
              Clear selection
            </Button>
          </Box>
        </Grid>
      </Grid>
      {isError && (
        <Typography color="error" mb={2}>
          {error?.message}
        </Typography>
      )}
      <Paper sx={{ width: '100%', minWidth: 0 }}>
        <DataGridPremium<UserContractor>
          rows={data?.rows || []}
          columns={columns}
          autoHeight
          checkboxSelection
          keepNonExistentRowsSelected
          disableRowSelectionOnClick
          isRowSelectable={({ row }) => !row.isArchived}
          rowSelectionModel={state.userContractorIds}
          onRowSelectionModelChange={(ids: GridRowId[]) => dispatch({ type: 'setUsers', ids: ids.map(Number) })}
          loading={isFetching}
          pagination
          paginationMode="server"
          rowCount={count}
          paginationModel={grid.paginationModel}
          onPaginationModelChange={grid.onPaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
          sx={{ border: 'none' }}
        />
      </Paper>
      <WizardFooter
        hint="Each person keeps the role they have at the source. The projects they are on move with them."
        onBack={() => dispatch({ type: 'goTo', step: TransferWizardStep.Contractors })}
        onNext={() => dispatch({ type: 'goTo', step: TransferWizardStep.Projects })}
      />
    </>
  )
}
